import type {
	CertificateEntry,
	CollectionState,
	LastScrapeState,
} from "../../../../../../../../../shared/types/certificates.type";
import { certsForColumnDisplay } from "./account-certificates";

export type CertPresenceKind =
	| "has_certs"
	| "verified_empty"
	| "check_failed"
	| "unwired"
	| "unknown"
	| "never_checked"
	| "loading";

export type CertPresenceAction = "edit" | "add";

export type CertPresence = {
	kind: CertPresenceKind;
	emptyLabel: string;
	hint: string;
	action: CertPresenceAction;
};

const FAILED_SCRAPE = new Set(["session_expired", "failed", "batch_failed"]);

function formatWhen(iso: string | null | undefined): string | null {
	if (!iso) return null;
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	return d.toLocaleString("uk-UA", {
		day: "numeric",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function unwiredHint(reason: string | null): string {
	if (reason === "collection_disabled") {
		return "Збір сертифікатів вимкнено глобально — даних немає";
	}
	if (reason === "binding_inactive") {
		return "Прив’язка Octo для цього MCC вимкнена — даних немає";
	}
	return "Збір сертифікатів для цього MCC не підключено — даних немає";
}

/**
 * Resolve UI presence from certificate rows + collection/last_scrape meta.
 *
 * Meta now comes from GET /api/v1/ui/certificates → `accounts` (DAI-1216)
 * for the dashboard, or from the account card for the popup. Same rules.
 *
 * When `metaReady` is false, empty rows stay neutral so we don’t flash
 * “немає” before we know.
 */
export function resolveCertPresence(args: {
	certificates: CertificateEntry[] | undefined;
	collection?: CollectionState | null;
	lastScrape?: LastScrapeState | null;
	metaReady?: boolean;
}): CertPresence {
	const visible = certsForColumnDisplay(args.certificates ?? []);
	if (visible.length > 0) {
		return {
			kind: "has_certs",
			emptyLabel: "",
			hint: "",
			action: "edit",
		};
	}

	const metaReady = args.metaReady !== false;
	if (!metaReady) {
		return {
			kind: "loading",
			emptyLabel: "—",
			hint: "Завантаження стану збору…",
			action: "edit",
		};
	}

	const enabled = args.collection?.enabled ?? null;
	const reason = args.collection?.reason ?? null;
	const scrape = args.lastScrape;
	const status = scrape?.status ?? null;
	const checkedAt = formatWhen(scrape?.succeeded_at ?? scrape?.attempted_at);

	// A real scrape outcome beats collection.enabled. Locally (and when the
	// MCC-level binding lookup is wrong) enabled can be false while accounts
	// were successfully checked — empty must read as «немає», not «+ / unwired».
	if (status === "completed") {
		return {
			kind: "verified_empty",
			emptyLabel: "немає",
			hint: checkedAt
				? `Сертифікатів немає. Перевірено ${checkedAt}`
				: "Сертифікатів немає",
			action: "edit",
		};
	}

	if (status === "octo_busy") {
		const lastOk = formatWhen(scrape?.succeeded_at);
		return {
			kind: "never_checked",
			emptyLabel: "···",
			hint: lastOk
				? `Octo зайнятий — спробуємо наступного разу. Останній успіх — ${lastOk}`
				: "Octo зайнятий — спробуємо наступного разу",
			action: "edit",
		};
	}

	if (status && FAILED_SCRAPE.has(status)) {
		// batch_failed with no successful check + collection off → still unwired
		// (scrape never really ran for this MCC). Otherwise show the failure.
		if (enabled === false && !scrape?.succeeded_at) {
			return {
				kind: "unwired",
				emptyLabel: "···",
				hint: unwiredHint(reason),
				action: "add",
			};
		}
		const fails = scrape?.failed_in_row ?? 0;
		const lastOk = formatWhen(scrape?.succeeded_at);
		const failBit =
			fails > 0
				? `Перевірити не вдалося (${fails} спроб поспіль)`
				: "Перевірити не вдалося";
		return {
			kind: "check_failed",
			emptyLabel: "!",
			hint: lastOk ? `${failBit}. Останній успіх — ${lastOk}` : failBit,
			action: "edit",
		};
	}

	if (enabled === false) {
		return {
			kind: "unwired",
			emptyLabel: "···",
			hint: unwiredHint(reason),
			action: "add",
		};
	}

	if (enabled === null) {
		return {
			kind: "unknown",
			emptyLabel: "?",
			hint: "Стан збору сертифікатів невідомий",
			action: "add",
		};
	}

	// enabled === true, never attempted
	return {
		kind: "never_checked",
		emptyLabel: "···",
		hint: "Збір ще не запускався для цього акаунта",
		action: "add",
	};
}
